import React, { useEffect, useState, useCallback } from "react";
import { Wrench, Globe2, Wifi, RotateCcw, Trash, LogOut, MessageSquare, RefreshCw, Settings, X, Shield, ShieldCheck, ShieldAlert } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

const headingFont = { fontFamily: "Outfit, sans-serif" };

/**
 * Human-friendly "N s ago" without leaning on the whole dayjs bundle.
 * Handles the ISO strings the backend returns everywhere.
 */
function timeAgo(iso) {
  if (!iso) return "—";
  const d = new Date(iso).getTime();
  if (!d) return iso;
  const s = Math.max(0, Math.floor((Date.now() - d) / 1000));
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

/**
 * Shared Remote Control dialog. Works for both the customer-facing Screens
 * page and the admin cross-tenant Fleet page. The only difference between
 * the two modes is which API endpoint queues + lists commands.
 *
 * Props:
 *   screen        The full screen doc (id, name, device_info, public_ip, …).
 *   onClose       Called when the user dismisses the dialog.
 *   adminMode     If true, hits /api/admin/fleet/* endpoints (any owner);
 *                 otherwise hits /api/screens/* (owner-only).
 */
export function RemoteControlDialog({ screen, onClose, adminMode = false }) {
  const [flashText, setFlashText] = useState("Tech assisting — please stand by…");
  const [flashSeconds, setFlashSeconds] = useState(15);
  const [commands, setCommands] = useState([]);
  const [busy, setBusy] = useState(null);

  const isOpen = !!screen;

  // Endpoints toggle based on mode — everything else is identical.
  const cmdEndpoint  = adminMode
    ? `/admin/fleet/screens/${screen?.id}/command`
    : `/screens/${screen?.id}/command`;
  const listEndpoint = adminMode
    ? `/admin/fleet/screens/${screen?.id}/commands?limit=8`
    : `/screens/${screen?.id}/commands?limit=8`;

  const refresh = useCallback(async () => {
    if (!screen?.id) return;
    try {
      const { data } = await api.get(listEndpoint);
      setCommands(data.commands || []);
    } catch { /* silent — most likely 401 during logout race */ }
  }, [screen?.id, listEndpoint]);

  // Auto-poll every 4s while dialog is open so pending→sent→done updates live.
  useEffect(() => {
    if (!isOpen) return;
    refresh();
    const itv = setInterval(refresh, 4000);
    return () => clearInterval(itv);
  }, [isOpen, refresh]);

  const send = async (command, params) => {
    if (!screen?.id) return;
    setBusy(command);
    try {
      await api.post(cmdEndpoint, { command, params: params || {} });
      toast.success(`${command} queued · TV picks up on next 5s poll`);
      setTimeout(refresh, 1200);
      setTimeout(refresh, 6000);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(null);
    }
  };

  if (!screen) return null;
  const di = screen.device_info || {};
  const av = di.app_version;
  const nativeSupported = av && av >= "1.4.3";

  return (
    <Dialog open={isOpen} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl bg-white border-slate-200 text-slate-900" data-testid="remote-control-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2" style={headingFont}>
            <Wrench className="h-5 w-5 text-sky-600" />
            Remote control · {screen.name || "screen"}
            {adminMode && (
              <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest bg-rose-100 text-rose-700 px-2 py-0.5 rounded-full font-semibold ml-2" data-testid="admin-mode-badge">
                <Shield className="h-3 w-3" />
                Admin
              </span>
            )}
          </DialogTitle>
          <DialogDescription className="text-slate-600">
            {adminMode && screen.owner_email ? (
              <>Operating on <strong className="text-slate-900">{screen.owner_email}</strong>&apos;s screen — commands are queued and picked up on the TV&apos;s next 5-second poll.</>
            ) : (
              <>Commands are queued in Nexso and picked up on the TV&apos;s next 5-second poll.</>
            )}
          </DialogDescription>
        </DialogHeader>

        {/* ── Network info banner ─────────────────────────── */}
        <div className="rounded-2xl bg-sky-50 border border-sky-200 p-4 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm">
          <div className="flex items-center gap-2">
            <Globe2 className="h-4 w-4 text-sky-600" />
            <span className="text-slate-500">LAN IP:</span>
            <span className="font-mono font-semibold text-slate-900" data-testid="remote-local-ip">
              {di.local_ip || <span className="text-slate-400 font-body italic">requires v1.4.3+</span>}
            </span>
            {di.local_ip && (
              <button
                onClick={() => {
                  const cmd = `adb connect ${di.local_ip}:5555`;
                  navigator.clipboard?.writeText(cmd);
                  toast.success("ADB command copied");
                }}
                className="ml-1 text-[10px] uppercase tracking-widest bg-slate-900 text-white px-2 py-0.5 rounded hover:bg-slate-700 transition-colors"
                title={`Copy: adb connect ${di.local_ip}:5555`}
                data-testid="copy-adb-btn"
              >
                Copy ADB
              </button>
            )}
          </div>
          <div className="flex items-center gap-2">
            <span className="text-slate-500">WAN IP:</span>
            <span className="font-mono font-semibold text-slate-900" data-testid="remote-public-ip">
              {screen.public_ip || <span className="text-slate-400 font-body italic">—</span>}
            </span>
          </div>
          {di.wifi_ssid && (
            <div className="flex items-center gap-2">
              <Wifi className="h-4 w-4 text-emerald-600" />
              <span className="text-slate-500">Wi-Fi:</span>
              <span className="font-semibold text-slate-900">{di.wifi_ssid}</span>
              {typeof di.wifi_rssi === "number" && (
                <span className="text-slate-400 text-xs">({di.wifi_rssi} dBm)</span>
              )}
            </div>
          )}
          {typeof di.ota_allowed === "boolean" && (
            <div className="flex items-center gap-2" data-testid="remote-ota-status">
              {di.ota_allowed ? (
                <>
                  <ShieldCheck className="h-4 w-4 text-emerald-600" />
                  <span className="text-slate-500">OTA updates:</span>
                  <span className="font-semibold text-emerald-700">Allowed</span>
                </>
              ) : (
                <>
                  <ShieldAlert className="h-4 w-4 text-rose-600" />
                  <span className="text-slate-500">OTA updates:</span>
                  <span className="font-semibold text-rose-700">Blocked — grant Install permission</span>
                </>
              )}
            </div>
          )}
        </div>

        {/* ── Web commands (any launcher) ─────────────────────────────── */}
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500 font-semibold mb-2">Instant · works on any launcher</div>
          <div className="grid grid-cols-2 gap-2">
            <CmdBtn testid="remote-cmd-reload" icon={RotateCcw} title="Force reload" subtitle="Hard-refresh the TV page" onClick={() => send("reload")} disabled={busy === "reload"} />
            <CmdBtn testid="remote-cmd-clear-cache" icon={Trash} title="Clear browser cache" subtitle="Wipe local state + reload" onClick={() => send("clear_cache")} disabled={busy === "clear_cache"} />
            <CmdBtn testid="remote-cmd-unpair" icon={LogOut} tone="rose" title="Unpair (local)" subtitle="Kick to pairing screen"
              onClick={() => { if (window.confirm("Unpair this screen? The TV will show a fresh pairing code.")) send("unpair"); }}
              disabled={busy === "unpair"} />
            <div className="col-span-2 rounded-xl border border-slate-200 p-3 space-y-2">
              <div className="flex items-center gap-2 text-sm font-semibold">
                <MessageSquare className="h-4 w-4 text-sky-600" />
                Show support message on TV
              </div>
              <Input value={flashText} onChange={(e) => setFlashText(e.target.value)} placeholder="Tech assisting — please stand by…" className="rounded-lg text-sm h-9 bg-slate-50 border-slate-200" data-testid="remote-flash-text-input" />
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-xs text-slate-500">Duration:</span>
                {[10, 15, 30, 60].map((s) => (
                  <button key={s} onClick={() => setFlashSeconds(s)} className={`rounded-full text-xs px-3 py-1 border ${flashSeconds === s ? "bg-sky-500 text-white border-sky-500" : "bg-white text-slate-700 border-slate-200 hover:bg-sky-50"}`} data-testid={`remote-flash-secs-${s}`}>{s}s</button>
                ))}
                <Button size="sm" className="ml-auto rounded-full bg-sky-500 hover:bg-sky-600 text-white text-xs" onClick={() => send("flash_message", { text: flashText, duration_seconds: flashSeconds })} disabled={!flashText.trim() || busy === "flash_message"} data-testid="remote-cmd-flash-message">
                  Send to TV
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* ── Native commands (v1.4.3+) ───────────────────────────────── */}
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500 font-semibold mb-2 flex items-center gap-2">
            Native · requires launcher v1.4.3+
            <span className={`text-[9px] px-2 py-0.5 rounded-full ${nativeSupported ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
              {av ? `v${av}` : "unknown"}
            </span>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <CmdBtn testid="remote-cmd-relaunch" icon={RefreshCw} title="Relaunch launcher" subtitle="Kill + restart Nexso app" onClick={() => send("relaunch_app")} disabled={busy === "relaunch_app"} />
            <CmdBtn testid="remote-cmd-clear-webview" icon={Trash} title="Clear WebView cache" subtitle="Deep clear (native)" onClick={() => send("clear_webview_cache")} disabled={busy === "clear_webview_cache"} />
            <CmdBtn testid="remote-cmd-wifi" icon={Wifi} title="Open Wi-Fi settings" subtitle="System panel on stick" onClick={() => send("open_wifi_settings")} disabled={busy === "open_wifi_settings"} />
            <CmdBtn testid="remote-cmd-reboot" icon={Settings} tone="rose" title="Reboot stick" subtitle="Needs root · best-effort"
              onClick={() => { if (window.confirm("Reboot the stick? May not work without root permissions.")) send("reboot_device"); }}
              disabled={busy === "reboot_device"} />
            <CmdBtn testid="remote-cmd-install-permission" icon={ShieldCheck} title="Open Install permission" subtitle="Unknown-apps toggle · v1.5.3+"
              onClick={() => send("open_install_permission")} disabled={busy === "open_install_permission"} />
          </div>
        </div>

        {/* ── History ─────────────────────────────── */}
        {commands.length > 0 && (
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500 font-semibold mb-2">Recent commands</div>
            <div className="rounded-xl border border-slate-200 divide-y divide-slate-100 max-h-40 overflow-y-auto" data-testid="remote-command-history">
              {commands.map((c) => {
                const statusColor = {
                  pending: "text-amber-600 bg-amber-50",
                  sent:    "text-sky-600 bg-sky-50",
                  done:    "text-emerald-700 bg-emerald-50",
                  error:   "text-rose-700 bg-rose-50",
                  expired: "text-slate-500 bg-slate-100",
                  unsupported: "text-slate-500 bg-slate-100",
                }[c.status] || "text-slate-500 bg-slate-100";
                return (
                  <div key={c.id} className="flex items-center gap-3 px-3 py-2 text-xs" data-testid={`remote-history-${c.id}`}>
                    <span className="font-mono text-slate-700 flex-1">{c.command}</span>
                    {c.issued_by_admin_email && (
                      <span className="text-[9px] text-rose-700 bg-rose-50 rounded-full px-1.5 py-0.5">admin</span>
                    )}
                    <span className={`px-2 py-0.5 rounded-full text-[10px] uppercase tracking-widest font-semibold ${statusColor}`}>
                      {c.status}
                    </span>
                    <span className="text-slate-400">{timeAgo(c.created_at)}</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" className="rounded-full bg-slate-50 border-slate-200 text-slate-900 hover:bg-slate-100" onClick={onClose} data-testid="remote-close-btn">
            <X className="h-4 w-4 mr-1" /> Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CmdBtn({ icon: Icon, title, subtitle, onClick, disabled, tone, testid }) {
  const isRose = tone === "rose";
  return (
    <Button
      variant="outline"
      onClick={onClick}
      disabled={disabled}
      data-testid={testid}
      className={`justify-start rounded-xl h-auto py-3 border-slate-200 ${isRose ? "hover:bg-rose-50 hover:border-rose-300" : "hover:bg-sky-50 hover:border-sky-300"} ${disabled ? "opacity-50" : ""}`}
    >
      <Icon className={`h-4 w-4 mr-2 ${isRose ? "text-rose-600" : "text-sky-600"}`} />
      <span className="text-left">
        <div className="font-semibold text-sm">{title}</div>
        <div className="text-xs text-slate-500">{subtitle}</div>
      </span>
    </Button>
  );
}
